/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './FeaturedPromos.scss';

import { ContentBlock, useFlags } from 'sarsaparilla';

import { PassesFeaturedPromo } from './PassesFeaturedPromo';
import { MobileAppFeaturedPromo } from './MobileAppFeaturedPromo';
import { ChristmasTreePermitsPromo } from './ChristmasTreePermitsPromo';
import { America250Promo } from './America250Promo';

const promosById: Record<string, React.FC> = {
    passes: PassesFeaturedPromo,
    mobileApp: MobileAppFeaturedPromo,
    treePermits: ChristmasTreePermitsPromo,
    america250: America250Promo,
};

interface FeaturedPromosProps {
    /** The position of the promo on the homepage. */
    position?: 'upper' | 'lower';
}

/**
 * Renders featured promotions based on identifiers from LaunchDarkly and the provided position.
 *
 * There are two positions: 'upper' and 'lower' and two LaunchDarkly flags:
 * 'homepageFeaturedPromosUpper' and 'homepageFeaturedPromosLower'.
 *
 * The LaunchDarkly values must be a comma-separated list of promo IDs, and are required
 * for any content to be displayed. If LaunchDarkly is not available no promos will be shown
 */
export function FeaturedPromos({ position }: FeaturedPromosProps) {
    const { homepageFeaturedPromosUpper, homepageFeaturedPromosLower } = useFlags();

    // The Launch Darkly flag value is expected to be a comma-separated list of promo IDs
    // e.g., "passes,mobileApp"
    const flagValue: unknown =
        position === 'upper'
            ? homepageFeaturedPromosUpper
            : position === 'lower'
              ? homepageFeaturedPromosLower
              : null;

    // Bail if there is no flag value or if it's not a string
    if (!flagValue || typeof flagValue !== 'string') {
        return null;
    }

    const promoIds = flagValue.split(',').map((promoId) => promoId.trim());

    const promosToRender = promoIds
        .map((promoId) => {
            const PromoComponent = promosById[promoId];
            return PromoComponent ? <PromoComponent key={promoId} /> : null;
        })
        .filter(Boolean);

    return (
        <div className="nav--featured-promos">
            <ContentBlock>
                <div className="grid">{promosToRender}</div>
            </ContentBlock>
        </div>
    );
}
